import { client } from "./supabase";

// ─── Queries ──────────────────────────────────────────────

export const getMovimientosBancarios = async (pais, filters = {}) => {
  let query = client
    .from("movimientos_bancarios")
    .select(
      `*,
      factura_emitida:facturas_sii_emitidas(folio, razon_social_receptor),
      factura_recibida:facturas_sii_recibidas(folio, razon_social_emisor),
      factura_externa:facturas_externas(numero_documento, archivo_url),
      movimiento_caja:movimientos_caja!movimiento_bancario_id(categoria_pago)`
    )
    .eq("pais", pais)
    .order("fecha", { ascending: false });

  if (filters.cuenta_bancaria_id) query = query.eq("cuenta_bancaria_id", filters.cuenta_bancaria_id);
  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.tipo) query = query.eq("tipo", filters.tipo);
  if (filters.fechaDesde) query = query.gte("fecha", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha", filters.fechaHasta);
  if (filters.moneda) query = query.eq("moneda", filters.moneda);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getMovimientosBancariosPendientes = async (pais) => getMovimientosBancarios(pais, { estado: "pendiente" });

// Devuelve sets de IDs de facturas SII que NO deberían ofrecerse en el modal de vinculación:
//   1. Vinculación full (movimiento bancario apunta a la factura sin cuota)
//   2. Plan de cuotas con todas las cuotas en estado distinto de 'pendiente'
export const getFacturasYaVinculadas = async (pais) => {
  const [vincRes, cobrosRes, pagosRes] = await Promise.all([
    client
      .from("movimientos_bancarios")
      .select("factura_sii_emitida_id, factura_sii_recibida_id, plan_cobro_cuota_id, plan_pago_cuota_id")
      .eq("pais", pais)
      .or("factura_sii_emitida_id.not.is.null,factura_sii_recibida_id.not.is.null")
      .range(0, 49999),
    client.from("plan_cobros_facturas").select("factura_sii_emitida_id, estado").eq("pais", pais).range(0, 49999),
    client.from("plan_pagos_facturas").select("factura_sii_recibida_id, estado").eq("pais", pais).range(0, 49999),
  ]);
  if (vincRes.error) throw vincRes.error;
  if (cobrosRes.error) throw cobrosRes.error;
  if (pagosRes.error) throw pagosRes.error;

  const emitidas = new Set();
  const recibidas = new Set();

  for (const m of vincRes.data || []) {
    if (m.factura_sii_emitida_id && !m.plan_cobro_cuota_id) emitidas.add(m.factura_sii_emitida_id);
    if (m.factura_sii_recibida_id && !m.plan_pago_cuota_id) recibidas.add(m.factura_sii_recibida_id);
  }

  const acumular = (filas, fkField) => {
    const stats = {};
    for (const c of filas || []) {
      const fid = c[fkField];
      if (!stats[fid]) stats[fid] = { total: 0, pendientes: 0 };
      stats[fid].total++;
      if (c.estado === "pendiente") stats[fid].pendientes++;
    }
    return stats;
  };

  for (const [fid, { total, pendientes }] of Object.entries(acumular(cobrosRes.data, "factura_sii_emitida_id"))) {
    if (total > 0 && pendientes === 0) emitidas.add(Number(fid));
  }
  for (const [fid, { total, pendientes }] of Object.entries(acumular(pagosRes.data, "factura_sii_recibida_id"))) {
    if (total > 0 && pendientes === 0) recibidas.add(Number(fid));
  }

  return { emitidas, recibidas };
};

export const getMovimientoBancarioById = async (id) => {
  const { data, error } = await client.from("movimientos_bancarios").select("*").eq("id", id).single();
  if (error) throw error;
  return data;
};

// ─── Importación (desde Edge Function clay-import) ────────

export const insertMovimientosBancarios = async (movimientos) => {
  if (!movimientos?.length) return { nuevos: 0, duplicados: 0 };

  const { data, error } = await client
    .from("movimientos_bancarios")
    .upsert(movimientos, { onConflict: "external_id", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevos = data?.length || 0;
  return { nuevos, duplicados: movimientos.length - nuevos };
};

// ─── Matching (actualizar sugerencias) ────────────────────

export const actualizarMatch = async (id, matchData) => {
  const { data, error } = await client.from("movimientos_bancarios").update(matchData).eq("id", id).select();
  if (error) throw error;
  return data?.[0];
};

// ─── Confirmar / Ignorar ─────────────────────────────────

export const confirmarMovimiento = async (id, { movimiento_caja_id, procesado_por }) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      movimiento_caja_id,
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const ignorarMovimiento = async (id, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "ignorado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const confirmarMovimientosBulk = async (ids, procesado_por) => {
  const { data, error } = await client
    .from("movimientos_bancarios")
    .update({
      estado: "procesado",
      procesado_por,
      procesado_at: new Date().toISOString(),
    })
    .in("id", ids)
    .select();
  if (error) throw error;
  return data || [];
};

// ─── Cuentas bancarias ───────────────────────────────────

export const getCuentasBancarias = async (pais) => {
  const { data, error } = await client
    .from("cuentas_bancarias")
    .select("*, bancos(nombre)")
    .eq("pais", pais)
    .eq("habilitado", true)
    .order("moneda", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const actualizarSaldoCuenta = async (id, saldo, fecha) => {
  const { error } = await client
    .from("cuentas_bancarias")
    .update({ saldo_actual: saldo, saldo_fecha: fecha, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

// ─── Resumen ─────────────────────────────────────────────

export const getResumenMovimientos = async (pais, fechaDesde, fechaHasta) => {
  const movimientos = await getMovimientosBancarios(pais, { fechaDesde, fechaHasta });

  const ingresos = movimientos.filter((m) => m.tipo === "ingreso");
  const egresos = movimientos.filter((m) => m.tipo === "egreso");
  const pendientes = movimientos.filter((m) => m.estado === "pendiente");

  return {
    totalIngresos: ingresos.reduce((sum, m) => sum + Number(m.monto), 0),
    totalEgresos: egresos.reduce((sum, m) => sum + Number(m.monto), 0),
    cantidadIngresos: ingresos.length,
    cantidadEgresos: egresos.length,
    pendientes: pendientes.length,
    total: movimientos.length,
  };
};
